// =============================================================================
// FILE STATUS HEADER
// status   : ACTIVE (S-M Batch 2 + S-N — KIMI, menu bantuan berasisten AI)
// version  : 1.2 (S-N 2.4 ingatan percakapan sessionStorage, 2.14 prefill penolakan; Batch 3.6
//            petunjuk layar sempit, 3.8 terima nav_suggestions dari backend utk dirender tombol)
// protected: NO -> SETUJU UBAH (S-M Batch 2 & S-N).
// =============================================================================
// KimiProvider — SATU-SATUNYA tempat state panel KIMI hidup (open/pending/messages/
// today_count) + SATU-SATUNYA tempat yang memanggil endpoint /api/v2/kimi/ask di frontend.
//
// INVARIAN WAJIB (8B & 8C, jangan dilonggarkan tanpa "SETUJU UBAH" baru dari Owner):
// - `openPanel()` HANYA boleh dipanggil dari onClick pengguna (tombol header atau
//   tautan "Kenapa begini?"). TIDAK ADA pemanggilan dari useEffect/timer/respons
//   API/error handler di berkas ini maupun pemanggilnya.
// - `ask()` (memanggil backend) HANYA dipanggil dari DUA tempat: (a) submit form di
//   KimiWidget, (b) langsung di dalam `openPanel()` saat dibuka dari "Kenapa begini?" —
//   KEDUANYA masih di dalam call-stack synchronous klik pengguna yang sama, BUKAN efek
//   terpisah yang bereaksi pada perubahan state `open`.
// - Menutup panel saat sedang menjawab MEMBATALKAN request (AbortController) — jawaban
//   yang datang terlambat SENGAJA dibuang (dicek via requestId, lihat `ask()`).
// - TIDAK ADA percobaan ulang otomatis (gagal -> tampilkan pesan, berhenti).
// - Jawaban KIMI TIDAK PERNAH memicu panggilan `ask()` berikutnya (tidak ada kode yang
//   memanggil `ask()` dari dalam handler jawaban berhasil).
//
// (S-N 2.4) INGATAN PERCAKAPAN — sessionStorage per pengguna, BUKAN database, BUKAN
// localStorage (perangkat kasir dipakai bergantian -> localStorage bisa membocorkan
// percakapan pengguna sebelumnya ke pengguna berikutnya; sessionStorage hilang saat tab
// ditutup DAN kita hapus manual saat logout/ganti pengguna di bawah). Disimpan MAKSIMAL
// 3 pasang tanya-jawab (selaras dgn HISTORY_MAX_PAIRS backend) — ini SEKALIGUS jadi apa
// yang ditampilkan di panel (bukan transkrip tak terbatas) DAN apa yang dikirim sbg
// konteks `history` ke backend. Backend TIDAK percaya penuh ke nilai ini — ia menegakkan
// ulang batasnya sendiri (`_clip_history` di kimi_v2.py).
import React, { createContext, useContext, useRef, useState, useCallback, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useV2Auth } from "@/v2/V2AuthContext";
import { MENU } from "@/v2/menu";

const API = process.env.REACT_APP_BACKEND_URL;
const KimiCtx = createContext(null);
export const useKimi = () => useContext(KimiCtx);

// (S-N 2.4) Batas ingatan percakapan — SAMA dengan konstanta backend (kimi_v2.py), supaya
// apa yang kita simpan/kirim tidak melebihi apa yang backend akan terima begitu saja.
const HISTORY_MAX_PAIRS = 3;
const HISTORY_MAX_ANSWER_CHARS = 600;
const HISTORY_MAX_QUESTION_CHARS = 500;
const HISTORY_MAX_TOTAL_CHARS = 2500;
const STORAGE_PREFIX = "kimi_conv_v1_";

// Label halaman untuk `page_hint` (HANYA petunjuk kalimat, TIDAK PERNAH untuk akses) —
// pakai SUMBER YANG SUDAH ADA (menu.js), bukan pemetaan baru. (S-N 2.10.c: nilai ini
// TIDAK PERNAH dipakai backend untuk keputusan akses — hanya menyusun kalimat jawaban.)
const pathToLabel = (pathname) => {
  const seg = (pathname || "").replace(/^\/+/, "").split("/")[0];
  const m = MENU.find((it) => it.path === seg);
  return m?.label || null;
};

const storageKeyFor = (uid) => `${STORAGE_PREFIX}${uid}`;

// (S-N 2.4) Paksa ulang batas di sisi frontend juga (defense in depth; backend tetap sumber
// kebenaran akhir) — buang pasangan PALING LAMA dulu bila lewat batas jumlah/karakter.
const clipPairs = (pairs) => {
  let out = (pairs || [])
    .filter((p) => p && p.a !== null && p.a !== undefined)
    .slice(-HISTORY_MAX_PAIRS)
    .map((p) => ({
      q: String(p.q || "").slice(0, HISTORY_MAX_QUESTION_CHARS),
      a: String(p.a || "").slice(0, HISTORY_MAX_ANSWER_CHARS),
    }));
  while (out.length > 0 && out.reduce((s, p) => s + p.q.length + p.a.length, 0) > HISTORY_MAX_TOTAL_CHARS) {
    out = out.slice(1);
  }
  return out;
};

const loadStoredMessages = (uid) => {
  if (!uid) return [];
  try {
    const raw = sessionStorage.getItem(storageKeyFor(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? clipPairs(parsed) : [];
  } catch {
    return [];
  }
};

const saveStoredMessages = (uid, pairs) => {
  if (!uid) return;
  try {
    sessionStorage.setItem(storageKeyFor(uid), JSON.stringify(clipPairs(pairs)));
  } catch {
    // sessionStorage penuh/diblokir browser -> diamkan, ingatan percakapan hanya opsional
  }
};

const clearStoredMessages = (uid) => {
  if (!uid) return;
  try { sessionStorage.removeItem(storageKeyFor(uid)); } catch { /* diamkan */ }
};

export const KimiProvider = ({ children }) => {
  const { authHeader, me } = useV2Auth();
  const location = useLocation();
  const [kimiEnabled, setKimiEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [messages, setMessages] = useState([]); // [{q, a}] maks 3 pasang (S-N 2.4), per pengguna
  const [todayCount, setTodayCount] = useState(null);
  const abortRef = useRef(null);
  const reqIdRef = useRef(0);
  const uidRef = useRef(undefined); // sentinel: beda dari uid apa pun di render pertama

  // Fetch TERPISAH dari V2AuthContext (public-config, tanpa auth) — sengaja begitu supaya
  // berkas ini berdiri sendiri (tidak menambah perubahan di V2AuthContext.jsx pada slice ini).
  useEffect(() => {
    fetch(`${API}/api/v2/public-config`).then((r) => (r.ok ? r.json() : null))
      .then((c) => { if (c) setKimiEnabled(!!c.kimi_enabled); }).catch(() => {});
  }, []);
  // ^ useEffect ini HANYA membaca flag tampil/sembunyi tombol (bukan memanggil KIMI,
  //   bukan membuka panel). Tidak melanggar 8B/8C.

  // (S-N 2.4) Ganti pengguna (login pertama, logout, atau ganti akun di perangkat yang
  // sama) -> HAPUS ingatan percakapan pengguna LAMA dari sessionStorage (privasi perangkat
  // bersama kasir/gudang), lalu muat ingatan milik pengguna BARU (bila ada, mis. setelah
  // refresh halaman dgn pengguna yang sama). Ini HANYA membaca/menulis storage & state —
  // TIDAK memanggil ask()/openPanel(), jadi tidak melanggar invarian 8C.
  useEffect(() => {
    const uid = me?.id || me?.email || null;
    if (uidRef.current === uid) return;
    const prevUid = uidRef.current;
    if (prevUid) clearStoredMessages(prevUid);
    uidRef.current = uid;
    setMessages(uid ? loadStoredMessages(uid) : []);
    // Ganti pengguna sementara panel terbuka -> tutup & batalkan request yg sedang berjalan,
    // supaya jawaban pengguna lama tidak "nyasar" ke sesi pengguna baru.
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    setPending(false);
    setOpen(false);
  }, [me?.id, me?.email]);

  // Persisten ke sessionStorage setiap kali daftar pasangan tanya-jawab berubah.
  useEffect(() => {
    if (uidRef.current) saveStoredMessages(uidRef.current, messages);
  }, [messages]);

  const closePanel = useCallback(() => {
    // Menutup panel saat sedang menjawab -> BATALKAN request; jawaban yang datang
    // terlambat dibuang (reqIdRef berbeda saat .then/.catch tiba -> diabaikan).
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    setPending(false);
    setOpen(false);
  }, []);

  // (S-N 2.4) Tombol "Mulai obrolan baru" — HANYA dipanggil dari onClick pengguna. Mengosongkan
  // ingatan percakapan (state + sessionStorage) pengguna ini; TIDAK memanggil ask()/backend.
  const startNewConversation = useCallback(() => {
    setMessages([]);
    if (uidRef.current) clearStoredMessages(uidRef.current);
  }, []);

  // Satu-satunya fungsi yang benar-benar memanggil backend. HANYA dipanggil dari onClick/
  // onSubmit atau dari dalam openPanel() (masih call-stack klik yang sama). TIDAK ADA retry
  // otomatis di sini (retry teknis 1x ada di BACKEND, lihat kimi_v2.py S-N 2.2 — bukan di UI).
  const ask = useCallback(async (message, { rejectReason } = {}) => {
    const text = (message || "").trim();
    if (!text || pending) return;
    if (abortRef.current) abortRef.current.abort(); // safety: batalkan sisa request lama bila ada
    const controller = new AbortController();
    abortRef.current = controller;
    const myReqId = ++reqIdRef.current;

    // (S-N 2.4) Riwayat yang dikirim = pasangan yang SUDAH selesai sebelum pertanyaan ini
    // (pertanyaan yang baru diketik BUKAN bagian riwayat, ia dikirim via field `message`).
    const historyToSend = clipPairs(messages);

    setMessages((prev) => [...prev, { q: text, a: null }]);
    setPending(true);
    try {
      const res = await fetch(`${API}/api/v2/kimi/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        signal: controller.signal,
        body: JSON.stringify({
          message: text,
          page_hint: pathToLabel(location.pathname),
          reject_reason: rejectReason || undefined,
          history: historyToSend.length ? historyToSend : undefined,
          // (Batch 3.6) HANYA petunjuk lebar layar (bukan akses) -> jawaban ditulis pendek
          // DARI AWAL oleh Gemini, bukan dipotong belakangan.
          narrow_screen: typeof window !== "undefined" ? window.innerWidth < 768 : false,
        }),
      });
      if (myReqId !== reqIdRef.current) return; // panel sudah ditutup/diganti -> buang
      if (res.status === 401) {
        setMessages((prev) => {
          const next = [...prev]; next[next.length - 1] = { q: text, a: "Sesi Anda berakhir. Muat ulang halaman lalu login kembali." };
          return next.slice(-HISTORY_MAX_PAIRS);
        });
        return;
      }
      if (!res.ok) throw new Error("http_" + res.status);
      const data = await res.json();
      if (myReqId !== reqIdRef.current) return; // dibatalkan/ditutup selagi menunggu -> buang jawaban
      setTodayCount(typeof data.today_count === "number" ? data.today_count : null);
      setMessages((prev) => {
        const next = [...prev];
        // (Batch 3.8) nav_suggestions HANYA ditempel di pesan ini utk dirender sbg tombol —
        // TIDAK disimpan ke sessionStorage/riwayat terkirim (clipPairs hanya ambil q/a).
        next[next.length - 1] = { q: text, a: data.reply || "", nav: data.nav_suggestions || null };
        return next.slice(-HISTORY_MAX_PAIRS); // (S-N 2.4) jaga maks 3 pasang tersimpan/tampil
      });
      // TIDAK ADA panggilan ask() lain di sini -> jawaban tidak pernah memicu pertanyaan lanjutan.
    } catch (err) {
      if (err?.name === "AbortError") return; // panel ditutup -> dibuang, TIDAK tampilkan apa pun
      if (myReqId !== reqIdRef.current) return;
      setMessages((prev) => {
        const next = [...prev]; next[next.length - 1] = { q: text, a: "Gagal menghubungi KIMI. Coba lagi nanti." };
        return next.slice(-HISTORY_MAX_PAIRS);
      });
      // TIDAK ADA retry otomatis dari sisi UI (8B.2) -> berhenti di sini, pengguna yang memutuskan coba lagi.
    } finally {
      if (myReqId === reqIdRef.current) setPending(false);
    }
  }, [authHeader, location.pathname, pending, messages]);

  // HANYA dipanggil dari onClick (tombol header / tautan "Kenapa begini?"). Terima BAIK
  // string lama (kompatibilitas) MAUPUN {reason, action} (S-N 2.14: sertakan aksi yang
  // sedang dicoba pengguna, bila ada, supaya pertanyaan yang terisi otomatis lebih jelas).
  // Riwayat percakapan yang SUDAH ADA (S-N 2.4) TIDAK dikosongkan oleh jalur ini.
  const openPanel = useCallback((rejectCtx) => {
    setOpen(true);
    if (!rejectCtx) return;
    const { reason, action } = typeof rejectCtx === "string" ? { reason: rejectCtx, action: null } : rejectCtx;
    if (!reason) return;
    const question = action
      ? `Kenapa ditolak saat mencoba "${action}"? Pesan yang muncul: "${reason}"`
      : `Kenapa muncul pesan ini: "${reason}"?`;
    ask(question, { rejectReason: reason });
  }, [ask]);

  const value = useMemo(() => ({
    kimiEnabled, open, pending, messages, todayCount, openPanel, closePanel, ask, startNewConversation,
  }), [kimiEnabled, open, pending, messages, todayCount, openPanel, closePanel, ask, startNewConversation]);

  return <KimiCtx.Provider value={value}>{children}</KimiCtx.Provider>;
};
